import React, { lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, unstable_HistoryRouter as HistoryRouter } from 'react-router-dom';
import { LayoutAdmin } from '@components/LayoutAdmin';
import { LayoutLogin } from '@components/LayoutLogin';
import { LayoutStudent } from "@components/LayoutStudent";
import ConfigProvider from 'antd/es/config-provider';
import AuthGuard from "@components/AuthGuard/AuthGuard";
import './index.css';
import "@fortawesome/fontawesome-free/css/all.min.css";
import { ToastContainer } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import { history, } from '@utils/BrowserHistory';
import { LayoutTeacher } from '@components/LayoutTeacher';
import NotFound from '@components/shares/404';
import RoleGuard from '@components/RoleGuard/RoleGuard';
import PermissionGuard from '@components/PermissionGuard/PermissionGuard';
import { PERM } from '@constants/permissions';
import { ADMIN_ROLE, ADMIN_SCHOOL, ADMIN_CLASS } from '@constants/roleconstants';
import ErrorBoundary from '@components/ErrorBoundary/ErrorBoundary';
import { CacheDevToolsProvider } from './utils/cacheDevTools';
import CacheDevPanel from './components/CacheDevPanel';

import LoadingH5pEvent from "@features/student/practice/LoadingH5pEvent";
import LoadingH5pAiEvent from "@features/student/practice/LoadingH5pAiEvent";
import ReportWrapper from '@features/student/report/ReportWrapper';

// features
const Login = lazy(() => import("@features/user/Login/Login"));
const LoginSSO = lazy(() => import("@features/user/Login/LoginSSO"));
const LoginApiCommute = lazy(() => import("@features/user/Login/LoginApiCommute"));
const K12SsoError = lazy(() => import("@features/user/K12SsoError/K12SsoError"));
const K12Landing = lazy(() => import("@features/user/K12ModulesLanding/K12Landing"));
const Register = lazy(() => import("@features/user/Register/Register"));
const ForgotPassword = lazy(() => import("@features/user/ForgotPassword/ForgotPassword"));
const AdminDashboard = lazy(() => import("@features/admin/dashboard/AdminDashboard"));
const StudentDashboard = lazy(() => import("@features/student/dashboard/StudentOverview"));
const StudentProfile = lazy(() => import("@features/student/profile/StudentProfile"));
const SchoolOverview = lazy(() => import("@features/admin/school/SchoolOverview/SchoolOverview"));
const SchoolData = lazy(() => import("@features/admin/school/SchoolDetails"));
const ClassOverview = lazy(() => import("@features/admin/class/ClassOverview/ClassOverview"));
const ClassData = lazy(() => import("@features/admin/class/ClassDetails"));
const GradeOverview = lazy(() => import("@features/admin/grade/GradeOverview/GradeOverview"));
const GradeData = lazy(() => import("@features/admin/grade/GradeDetails"));
const ModuleOverview = lazy(() => import("@features/admin/module/ModuleOverview"));
const ModuleDetail = lazy(() => import("@features/admin/module/ModuleDetail"));
const ModuleManager = lazy(() => import("@features/admin/module/ModuleManager/ModuleManagerGeneralInfo").then(module => ({ default: module.ModuleManagerGeneralInfo })));
const StudentPracticeOverview = lazy(() => import("@features/student/practice/StudentPracticeOverview"));
const CheckDevice = lazy(() => import("@features/student/practice/DeviceCheck"));
const PracticingScreen = lazy(() => import("@features/student/practice/PracticingScreen"));
const PracticeResult = lazy(() => import("@features/student/practice/PracticeResult"));
const ExamTestOverview = lazy(() => import("@features/student/examtest/ExamTestOverview"))
const RoleOverview = lazy(() => import("@features/admin/role/RoleOverview/RoleOverview"));
const RoleData = lazy(() => import("@features/admin/role/RoleDetails"));
const GroupPermissionsOverview = lazy(() => import("@features/admin/groupPermissions/GroupPermissionsOverview/GroupPermissionsOverview"));
const GroupPermissionsData = lazy(() => import("@features/admin/groupPermissions/GroupPermissionsDetails"));
const AccountOverview = lazy(() => import("@features/admin/account/AccountOverview/AccountOverview"));
const AccountData = lazy(() => import("@features/admin/account/AccountDetails"));
const CertificationModule = lazy(() => import("@features/student/examtest/CertificationModule"));
const ExamTesting = lazy(() => import('@features/student/examtest/ExamTesting'));
const StudentManagement = lazy(() => import("@features/admin/student/StudentManagement"));
const ExamResult = lazy(() => import('@features/student/examtest/ExamResult'));
const ReportingPractice = lazy(() => import('@features/admin/reporting/reportPractice/index'));
const ReportingExams = lazy(() => import('@features/admin/reporting/reportExams/ReportingExams'));
const Calendar = lazy(() => import('@features/student/calendar'));
const Lessons = lazy(() => import('@features/student/lessons'));
const BooksStudent = lazy(() => import('@features/student/books/BooksStudent'));
const StudentBookDetail = lazy(() => import('@features/student/books/StudentBookDetail'));
const MeetingScreen = lazy(() => import('@features/student/meeting'));
const OfflineMeetingScreen = lazy(() => import('@features/student/offlineMeeting'));
const LessonDetails = lazy(() => import("@features/student/lessons/lessonDetails"))
const Report = lazy(() => import('@features/student/report'));
const LearnWithAI = lazy(() => import('@features/student/learning/LearnWithAI'));
const PracticeLesson = lazy(() => import('@features/student/lessons/practiceLesson'));
const LessonPracticeResult = lazy(() => import('@features/student/lessons/lessonPracticeResult'));
const LearningMaterialsOverview = lazy(() => import('@features/admin/learningMaterials/LearningMaterialsOverview/LearningMaterialsOverview'))
const LearningMaterialData = lazy(() => import('@features/admin/learningMaterials/LearningMaterialsDetails'))
const TeacherDashboard = lazy(() => import('@features/teacher/dashboard/TeacherDashboard'));
const TeacherReport = lazy(() => import('@features/teacher/reporting'));
const TeacherReportExams = lazy(() => import('@features/teacher/reporting/reportExams/ReportExams'));
const TeacherClass = lazy(() => import('@features/teacher/class/ClassOverview/ClassOverview'));
const TeacherClassDetail = lazy(() => import('@features/teacher/class/ClassDetails'));
const ListClassScoring = lazy(() => import('@features/admin/distributionEvaluation/scoring/ListClass'));
const DistributionEvaluation = lazy(() => import('@features/admin/distribution-evaluation/DistributionEvaluation'));
const ZoomAccountTabs = lazy(() => import('@features/admin/zoomAccount/ZoomAccountTabs'));
const ModuleTeacherDetail = lazy(() => import("@features/teacher/module/ModuleTab"));
const PasscodeSettings = lazy(() => import("@features/admin/settings/PasscodeSettings"));
const PartnerOverview = lazy(() => import("@features/admin/partner/PartnerOverview/PartnerOverview"));
const PartnerData = lazy(() => import("@features/admin/partner/PartnerDetails"));
const TaskJobsOverview = lazy(() => import("@features/admin/taskJobs/TaskJobsOverview"));
const App: React.FC = () => {
    const showCacheOverlay = new URLSearchParams(window.location.search).get('show_cache_key') === '1';
    return (
        <CacheDevToolsProvider>
        <ConfigProvider
            theme={{
                token: {
                    colorPrimary: '#F17A20',
                    fontFamily: "'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
                },
            }}
        >
            <ToastContainer
                position="top-right"
                autoClose={3000}
                hideProgressBar={true}
                newestOnTop={false}
                closeOnClick
                rtl={false}
                pauseOnFocusLoss
                draggable
                pauseOnHover
                theme="light"
            />
            <ErrorBoundary>
            <Suspense fallback={<span>loading</span>}>
                <HistoryRouter history={history as any}>
                    <Routes>
                        <Route path="/user" element={<LayoutLogin />}>
                            <Route path={'register'} element={<Register />} />
                            <Route path={'forgot-password'} element={<ForgotPassword />} />
                            <Route path={'login'} element={<Login />} />
                            <Route path={'login-sso'} element={<LoginSSO />} />
                            <Route path={'admin-login'} element={<Login />} />
                        </Route>
                        <Route path="/landing" element={<LayoutLogin />}>
                            <Route path="modules" element={<K12Landing ssoPath="list_module" />} />
                            <Route path="module_detail/:id" element={<K12Landing ssoPath="module_detail" />} />
                            <Route path="report_detail/:id" element={<K12Landing ssoPath="report_detail" />} />
                        </Route>
                        <Route path="admin" element={ <RoleGuard allowedRoles={[ADMIN_ROLE, ADMIN_SCHOOL]}>
                            <LayoutAdmin />
                        </RoleGuard>}>
                            <Route index element={<AdminDashboard />} />
                            <Route path='dashboard' element={<AdminDashboard />} />
                            <Route path='school' element={
                                <RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                    <SchoolOverview />
                                </RoleGuard>
                            } />
                            <Route path='newschool' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <SchoolData />
                            </RoleGuard>
                            } />
                            <Route path='detail' element={
                                <RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                    <SchoolData />
                                </RoleGuard>
                            } />
                            <Route path='modules' element={
                                <ModuleOverview />
                            } />
                            <Route path='newmodule' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <ModuleDetail />
                            </RoleGuard>
                            } />
                            <Route path='module' element={
                                <ModuleDetail />
                            } />
                            <Route path='module-management' element={< RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <ModuleManager />
                            </RoleGuard>
                            } />
                            <Route path='class' element={
                                <ClassOverview />
                            } />
                            <Route path='newclass' element={
                                <ClassData />
                            } />
                            <Route path='classdetail' element={
                                <ClassData />
                            } />
                            <Route path='grade' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <GradeOverview />
                            </RoleGuard>
                            } />
                            {/* <Route path='newgrade' element={<GradeData />} /> */}
                            <Route path='gradedetail' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <GradeData />
                            </RoleGuard>
                            } />
                            <Route path='role' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <RoleOverview />
                            </RoleGuard>
                            } />
                            <Route path='roledetail' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <RoleData />
                            </RoleGuard>
                            } />
                            <Route path='newrole' element={<RoleData />} />
                            <Route path='group-permissions' element={<GroupPermissionsOverview />} />
                            <Route path='groupdetail' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <GroupPermissionsData />
                            </RoleGuard>
                            } />
                            <Route path='newgroup' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <GroupPermissionsData />
                            </RoleGuard>
                            } />
                            <Route path='account' element={<AccountOverview />} />
                            <Route path='account/accountdetail' element={<AccountData />} />
                            <Route path='account/newaccount' element={<AccountData />} />
                            <Route path='task-jobs' element={<TaskJobsOverview />} />
                            {/* <Route path='student' element={<StudentManagement />} /> */}
                            <Route path='reporting_practice' element={<ReportingPractice />} />
                            <Route path='reporting_exams' element={<ReportingExams />} />
                            <Route path='calendar' element={<Calendar />} />
                            {/* <Route path='learning-material' element={<LearningMaterialsOverview />} /> */}
                            <Route path='learning-material/:mode/:learningMaterialId?' element={<LearningMaterialData />} />
                            <Route path='distribution-evaluation' element={<DistributionEvaluation />} />
                            <Route path='zoom-account' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <ZoomAccountTabs />
                            </RoleGuard>
                            } />
                            <Route path='partners' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <PartnerOverview />
                            </RoleGuard>
                            } />
                            <Route path='newpartner' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <PartnerData />
                            </RoleGuard>
                            } />
                            <Route path='partnerdetail' element={<RoleGuard allowedRoles={[ADMIN_ROLE]}>
                                <PartnerData />
                            </RoleGuard>
                            } />
                            {/* <Route path='zoom-subaccount' element={<ZoomAccountTabs />} /> */}
                        </Route>
                        {/* Exam test/result: full-screen, no sidebar/header */}
                        <Route path="student/examtest/test" element={<PermissionGuard permission={PERM.student.exam}><ExamTesting /></PermissionGuard>} />
                        <Route path="student/examtest/result" element={<PermissionGuard permission={PERM.student.exam}><ExamResult /></PermissionGuard>} />
                        <Route path="student/book/bookdetail" element={<PermissionGuard permission={PERM.student.book}><StudentBookDetail /></PermissionGuard>} />
                        <Route path="student" element={<LayoutStudent offAiAssistant={false} useTimeFilter={false} />}>
                            <Route index path='dashboard' element={<PermissionGuard permission={PERM.student.dashboard}><StudentDashboard /></PermissionGuard>} />
                            <Route path='dashboard' element={<PermissionGuard permission={PERM.student.dashboard}><StudentDashboard /></PermissionGuard>} />
                            <Route path='practice' element={<PermissionGuard permission={PERM.student.practice}><StudentPracticeOverview /></PermissionGuard>} />
                            <Route path='practice/checkdevice' element={<PermissionGuard permission={PERM.student.practice}><CheckDevice /></PermissionGuard>} />
                            <Route path='practice/topic' element={<PermissionGuard permission={PERM.student.practice}><PracticingScreen /></PermissionGuard>} />
                            <Route path='practice/result' element={<PermissionGuard permission={PERM.student.practice}><PracticeResult /></PermissionGuard>} />
                            <Route path='practice/h5p' element={<LoadingH5pEvent />} />
                            <Route path='practice/h5p-ai' element={<LoadingH5pAiEvent />} />
                            <Route path='examtest/overview' element={<PermissionGuard permission={PERM.student.exam}><ExamTestOverview /></PermissionGuard>} />
                            <Route path='examtest/certificate' element={<PermissionGuard permission={PERM.student.exam}><CertificationModule /></PermissionGuard>} />
                            <Route path='calendar' element={<PermissionGuard permission={PERM.student.calendar}><Calendar /></PermissionGuard>} />
                            <Route path='lessons' element={<Lessons />} />
                            <Route path='lesson/practice' element={<PracticeLesson />} />
                            <Route path='lesson/practice/result' element={<LessonPracticeResult />} />
                            <Route path='learning/ai-tutor' element={<LearnWithAI />} />
                            <Route path='report' element={<PermissionGuard permission={PERM.student.report}><ReportWrapper /></PermissionGuard>} />
                            <Route path='book' element={<PermissionGuard permission={PERM.student.book}><BooksStudent /></PermissionGuard>} />
                            <Route path='profile' element={<StudentProfile />} />
                        </Route>
                        <Route path="student/lesson" element={<LayoutStudent offAiAssistant={true} replaceButton={true} />}>
                            <Route index element={<LessonDetails />} />
                        </Route>
                        <Route path='meeting' element={<MeetingScreen />}>
                        </Route>
                        <Route path='offline-meeting' element={<OfflineMeetingScreen />}>
                        </Route>
                        <Route path="student" element={<LayoutStudent offAiAssistant={true} />}>
                            <Route path='practice/h5p' element={<PermissionGuard permission={PERM.student.practice}><LoadingH5pEvent /></PermissionGuard>} />
                            <Route path='practice/h5p-ai' element={<PermissionGuard permission={PERM.student.practice}><LoadingH5pAiEvent /></PermissionGuard>} />
                        </Route>
                        <Route path="teacher" element={<LayoutTeacher offAiAssistant={true} />}>
                            <Route path='report/practice' element={<PermissionGuard permission={PERM.teacher.reportPractice}><TeacherReport /></PermissionGuard>} />
                            <Route path='report/exams' element={<PermissionGuard permission={PERM.teacher.reportExam}><TeacherReportExams /></PermissionGuard>} />
                            <Route path='class' element={<PermissionGuard permission={PERM.teacher.class}><TeacherClass /></PermissionGuard>} />
                            <Route path='class/classdetail' element={<PermissionGuard permission={PERM.teacher.class}><TeacherClassDetail /></PermissionGuard>} />
                            <Route path='modules' element={<PermissionGuard permission={PERM.teacher.modules}><ModuleOverview /></PermissionGuard>} />
                            <Route path='modules/module' element={<PermissionGuard permission={PERM.teacher.modules}><ModuleTeacherDetail /></PermissionGuard>} />
                            <Route path='scoring' element={<ListClassScoring />} />
                            <Route path='distribution-evaluation' element={<PermissionGuard permission={PERM.teacher.distribution}><DistributionEvaluation /></PermissionGuard>} />
                        </Route>
                        <Route path="teacher" element={<LayoutTeacher offAiAssistant={true} handleSearchFilterOption={1} />}>
                            <Route index path='dashboard' element={<PermissionGuard permission={PERM.teacher.dashboard}><TeacherDashboard /></PermissionGuard>} />
                            <Route path='dashboard' element={<PermissionGuard permission={PERM.teacher.dashboard}><TeacherDashboard /></PermissionGuard>} />
                            <Route path='calendar' element={<PermissionGuard permission={PERM.teacher.calendar}><Calendar /></PermissionGuard>} />
                        </Route>
                        <Route path="/ssscode" element={<PasscodeSettings />} />
                        <Route path="/" element={<AuthGuard />} />
                        <Route path="/callback" element={<LoginApiCommute />} />
                        <Route path="/k12-sso-error" element={<K12SsoError />} />
                        <Route path="*" element={<NotFound />} />
                    </Routes>
                </HistoryRouter>
            </Suspense>
            </ErrorBoundary>
        </ConfigProvider>
        {showCacheOverlay && <CacheDevPanel />}
        </CacheDevToolsProvider>
    );
};

export default App;



